import template from 'lodash/template'
import pick from 'lodash/pick'
import isString from 'lodash/isString'

import Search from 'ol-ext/control/Search'
import OlExtElement from 'ol-ext/util/element'

import { expandAll } from 'regex-to-strings'

import { GeoJSON, WFS } from 'ol/format'
import {
  and as andFilter,
  or as orFilter,
  equalTo as equalToFilter,
  like as likeFilter,
} from 'ol/format/filter'

class SearchWFS extends Search {
  constructor (optOptions = {}) {
    const { getTitle, autocomplete, noCollapse, maxHistory, ...options } = optOptions

    // on ne veut pas de fermeture auto ni d'historique
    options.noCollapse = true
    options.maxHistory = -1
    options.placeholder = options.placeholder || 'Rechercher'

    super(options)

    if (!options.viewer) {
      console.error('[SearchWFS] options.viewer non présent')
    }
    this.viewer = options.viewer
    this.mainMinLength = this.get('minLength')
    this.defaultPlaceholder = options.placeholder
    this.getInputField().setAttribute('placeholder', options.wfsSources[0]?.placeholder || this.defaultPlaceholder)
    this.emptyTitle = options.label || 'Rechercher'

    if (!options.wfsSources) {
      console.error('[SearchWFS] options.wfsSources non présent')
    }
    this.wfsSources = options.wfsSources
    // contient les résultats des recherche parente nécessaires
    this.wfsResults = { }

    this.controller = null

    if (options.wfsSources.length > 1) {
      // ajoute une zone de titre
      this.element.classList.add('has-title')
      const titleZone = document.createElement('div')
      titleZone.className = 'search-title'
      this.title = document.createElement('LABEL')
      this.title.innerText = this.emptyTitle
      // permet de clear les sous-résultats
      const closeButton = OlExtElement.create('I', {
        className: 'kmapv-icon xs kmapv-icon-cancel',
        style: {
          position: 'absolute',
          right: '0',
          top: '4px',
        },
        title: 'Effacer les valeurs précédentes',
      })
      closeButton.addEventListener('click', () => {
        this.clearSearch()
      })

      titleZone.append(this.title)
      titleZone.append(closeButton)
      this.element.insertBefore(titleZone, this.button)
    }
  }

  getTitle (f) { return f.label }

  // Search result
  autocomplete (s, cback) {
    // annule la précédente requête
    if (this.element.classList.contains('searching')) {
      this.controller.abort('Annulation de la requête')
      this.controller = null
    }
    // on utilise comme source de recherche la derniere non setté
    const wfsSourceIndex = this.wfsSources.findIndex(source => !(source.name in this.wfsResults))
    const wfsSource = this.wfsSources[wfsSourceIndex]

    const prefilters = Object.entries(wfsSource.prefilter || {}).map(([propertyName, value]) => {
      // isEqual
      if (isString(value)) {
        // __PREVIOUS_RESULT__
        if (value.startsWith('__PREVIOUS_RESULT__.')) {
          const previousResultName = value.split('.')[1]

          const indexOfSplit = value.replace('__PREVIOUS_RESULT__.', '').indexOf('.')
          const previousResultTemplate = value.replace('__PREVIOUS_RESULT__.', '').slice(indexOfSplit + 1)
          // soit on a un template, soit le nom de la propriété
          if (previousResultTemplate.indexOf('<%') > -1) {
            const compiled = template(previousResultTemplate)
            value = compiled(this.wfsResults[previousResultName].value)
          } else {
            value = this.wfsResults[previousResultName].value[previousResultTemplate]
          }
        }
        return equalToFilter(propertyName, value)
      } else {
        // in
        return orFilter.apply(null, value.map(uniqValue => equalToFilter(propertyName, uniqValue)))
      }
    })

    let bbox
    this.wfsSources.slice(0, wfsSourceIndex).reverse().forEach(source => {
      const name = source.name
      if (!bbox && this.wfsResults[name]?.feature?.bbox) {
        bbox = this.wfsResults[name]?.feature?.bbox
      }
    })

    // si on a plusieurs prefilter, on englobe d'un "and" (toutes les conditions doivent être remplie)
    const prefilter = prefilters.length === 0
      ? null
      : prefilters.length === 1
        ? prefilters[0]
        : andFilter.apply(null, prefilters)

    const searchStringOptions = wfsSource?.searchString === 'common'
      ? {
          replace: {
            '-': '\\*',
            ' ': '\\*',
          },
          same: [
            'aàâä',
            'eéèêë',
            'iîï',
            'oôö',
            'uùûü',
            'yÿ',
            'cç',
          ],
        }
      : wfsSource?.searchString || {}

    let realSearchString = s

    Object.entries(searchStringOptions?.replace || {}).forEach(([search, replace]) => {
      // eslint-disable-next-line prefer-regex-literals
      const r = new RegExp(search, 'i')
      realSearchString = realSearchString.replace(r, replace)
    })
    const sameToTest = searchStringOptions?.same || []

    sameToTest.forEach(same => {
      // eslint-disable-next-line prefer-regex-literals
      const r = new RegExp(`[${same}]`, 'i')
      realSearchString = realSearchString.replace(r, `[${same}]`)
    })

    const realSearchStrings = expandAll(realSearchString)

    const searchStringFilters = wfsSource.searchIn.reduce((acc, propertyName) => {
      return acc.concat(realSearchStrings.map(str => likeFilter(propertyName, `*${str}*`, '*', '.', '!', false)))
    }, [])
    // console.log(searchStringFilters2)
    // const searchStringFilters = wfsSource.searchIn.map((propertyName) => likeFilter(propertyName, `*${s}*`, '*', '.', '!', false))
    // on recherche dans plusieurs champs, on englobe d'un "or"
    const searchStringFilter = searchStringFilters.length === 1 ? searchStringFilters[0] : orFilter.apply(null, searchStringFilters)

    // on a des préfiler, on englove d'un "and" : prefilter AND searchString
    const filters = prefilter ? andFilter(prefilter, searchStringFilter) : searchStringFilter
    const maxItems = this.get('maxItems')
    const { wfs, name, geometryName, display, featureTypes, featureNS, featurePrefix, srsName = 'EPSG:3857', useBbox } = wfsSource
    let { value } = wfsSource
    // renvoi la géométrie au dernier niveau pour pouvoir zoomer dessus
    const returnGeometry = this.wfsSources[this.wfsSources.length - 1].name === name
    // champs que l'on veut récupérer
    if (!Array.isArray(value)) {
      value = [value]
    }

    const featureRequest = new WFS(wfs).writeGetFeature({
      srsName,
      maxFeatures: maxItems, // pour wfs 1.1.0
      count: maxItems, // pour wfs 2.0.0
      featureNS,
      featurePrefix,
      featureTypes,
      outputFormat: 'application/json',
      geometryName,
      propertyNames: returnGeometry ? [...value, display, geometryName] : [...value, display],
      bbox: useBbox ? bbox : undefined,
      filter: filters,
    })

    this.element.classList.add('searching')
    this.controller = new AbortController()
    fetch(wfs.url, {
      method: 'POST',
      body: new XMLSerializer().serializeToString(featureRequest),
      headers: new Headers({
        'Content-Type': 'application/xml',
      }),
      signal: this.controller.signal,
    })
      .then((response) => {
        return response.json()
      })
      .then((json) => {
        this.element.classList.remove('searching')

        const results = json.features.map(feature => ({
          label: feature.properties[display],
          value: pick(feature.properties, value),
          source: name,
          feature,
          srid: srsName,
        })).sort((a, b) => a.label.localeCompare(b.label))
        cback(results)
        return false
      }).catch((e) => {
        if (e instanceof DOMException && e.name === 'AbortError') {
          console.log('Requête annulée', s)
        } else {
          this.element.classList.remove('searching')
          console.log('Erreur requête wfs: ' + e.message, e)
          cback([{ label: "Erreur d'execution de la requête, voir la console" }])
        }
      })
  }

  /** A line has been clicked in the menu > dispatch event
   * @param {any} f the feature, as passed in the autocomplete
   * @param {boolean} reverse true if reverse geocode
   * @param {ol.coordinate} coord
   * @param {*} options options passed to the event
   */
  select (f, reverse, coord, options) {
    // l'utilisateur a cliqué sur une option alors que c'est en train de chercher autre chose
    if (this.element.classList.contains('searching')) {
      this.controller.abort('Annulation de la requête')
      this.controller = null
    }

    const { source, value, label, feature, srid } = f
    if (value === undefined) {
      this.drawList_([])
      this.buildTitle()
      return
    }
    // on est sur la dernière source: zoom
    if (this.wfsSources[this.wfsSources.length - 1].name === source) {
      const olFeature = new GeoJSON().readFeature(feature, { dataProjection: srid, featureProjection: this.viewer.getViewProjection() })
      // console.log(feature)
      this.viewer.zoomToExtent(olFeature.getGeometry().getExtent())
      this.element.classList.add('ol-collapsed')
    } else {
      // sinon on passe a l'étape suivante
      this.wfsResults[source] = { value, label, feature }
      this.getInputField().value = ''
      this.getInputField().focus()

      // set le minLength si besoin
      const wfsSourceIndex = this.wfsSources.findIndex(({ name }) => name === source)
      const nextWfsSource = wfsSourceIndex > (this.wfsSources.length - 1) ? this.wfsSources[wfsSourceIndex] : this.wfsSources[wfsSourceIndex + 1]

      this.set('minLength', nextWfsSource.minLength || this.mainMinLength)
      this.getInputField().setAttribute('placeholder', nextWfsSource.placeholder || this.defaultPlaceholder)
      this.drawList_([])
      this.buildTitle()
    }
  }

  buildTitle () {
    this.title.innerText = Object.values(this.wfsResults).length ? Object.values(this.wfsResults).map(({ label }) => label).join(' \\ ') : this.emptyTitle
  }

  clearSearch () {
    this.set('minLength', this.mainMinLength)
    this.getInputField().setAttribute('placeholder', this.wfsSources[0]?.placeholder || this.defaultPlaceholder)
    this.wfsResults = {}
    this.getInputField().value = ''
    this.drawList_([])
    this.buildTitle()
    this.element.classList.add('ol-collapsed')
  }
}

export default SearchWFS
